import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Icon } from '@/components/synapse/Icon';
import { Segmented } from '@/components/synapse/Segmented';
import { TypePill } from '@/components/synapse/TypePill';
import { useDebounced } from '@/hooks/useDebounced';
import { useItemsSearch } from '@/hooks/useInventory';
import { useCombobox } from '@/hooks/useCombobox';
import { ComboboxListbox } from '@/components/synapse/ComboboxListbox';
import { BUCKET_GRANULARITIES, MEASURES, MEASURE_IDS } from '@/lib/analytics';
import type { BucketGranularity, MeasureId } from '@/lib/analytics';
import { ITEM_TYPES } from '@/lib/itemTypeConfig';
import type { ItemType } from '@/lib/itemTypeConfig';
import { cn } from '@/lib/utils';
import type { Item } from '@/types/api';
import { calendarLabel, earliestFromFor, granularityFits } from './issuingWindow';
import type { PeriodPreset, ResolvedWindow } from './issuingWindow';
import type { IssuingView } from './issuingUrlState';

/**
 * The one control bar every panel reads from. It owns no state: each control
 * reports a patch and the page writes it to the URL, which is what makes the
 * whole view a link somebody can send.
 */
export interface ControlProps {
  view: IssuingView;
  window: ResolvedWindow;
  onChange: (patch: Partial<IssuingView>) => void;
}

const PERIOD_LABEL_KEY: Record<PeriodPreset, string> = {
  today: 'analytics.period.today',
  week: 'analytics.period.week',
  month: 'analytics.period.month',
  last30: 'analytics.period.last30',
  last90: 'analytics.period.last90',
  custom: 'analytics.period.custom',
};

export const GRANULARITY_LABEL_KEY: Record<BucketGranularity, string> = {
  day: 'analytics.granularity.day',
  week: 'analytics.granularity.week',
  month: 'analytics.granularity.month',
};

const chipClass = (active: boolean) =>
  cn(
    'inline-flex h-8 items-center rounded-full px-3 text-[12.5px] font-medium transition-colors',
    active
      ? 'bg-accent text-accent-foreground'
      : 'border border-line bg-paper text-muted hover:bg-paper-2 hover:text-ink',
  );

/**
 * The period row. Stays visible at every width — it is the control people reach
 * for most, and burying it in a sheet would cost a tap on the one thing they
 * always change.
 *
 * Under it, always, the window the page actually resolved to. A chip says "This
 * week" and a custom field says what somebody typed; neither says what the
 * panels are counting, and the two can differ — `clampRange` trims an over-long
 * custom range, and a preset means different dates tomorrow. Every figure on
 * this page is about a window, so the window is written out.
 */
export function PeriodChips({ view, window, onChange }: ControlProps) {
  const { t, i18n } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2" role="group" aria-label={t('analytics.period.label')}>
        {(Object.keys(PERIOD_LABEL_KEY) as PeriodPreset[]).map((preset) => (
          <button
            key={preset}
            type="button"
            aria-pressed={view.period === preset}
            onClick={() =>
              onChange(
                // Seed the custom form from whatever is on screen, so switching to
                // it is a starting point rather than an empty pair of date fields.
                preset === 'custom'
                  ? { period: preset, customFrom: window.from, customTo: window.to }
                  : { period: preset },
              )
            }
            className={chipClass(view.period === preset)}
          >
            {t(PERIOD_LABEL_KEY[preset])}
          </button>
        ))}
      </div>

      {view.period === 'custom' && (
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="date"
            value={view.customFrom}
            min={earliestFromFor(view.customTo)}
            max={view.customTo || undefined}
            onChange={(e) => onChange({ customFrom: e.target.value })}
            aria-label={t('analytics.period.from')}
            className="w-auto"
          />
          <span className="text-[12.5px] text-muted-2">–</span>
          <Input
            type="date"
            value={view.customTo}
            min={view.customFrom || undefined}
            onChange={(e) => onChange({ customTo: e.target.value })}
            aria-label={t('analytics.period.to')}
            className="w-auto"
          />
        </div>
      )}

      <p className="text-[11.5px] text-muted-2">
        {t('analytics.windowShowing', {
          from: calendarLabel(window.from, i18n.language),
          to: calendarLabel(window.to, i18n.language),
        })}
      </p>
    </div>
  );
}

interface FilterFieldsProps extends ControlProps {
  /** Resolved name of the selected medicine, when it is known. */
  selectedItemLabel: string | null;
}

/**
 * Everything the period row doesn't carry. Rendered inline on a wide screen and
 * inside the filter sheet on a narrow one — the same component either way, so
 * the two can't drift into offering different controls.
 */
export function IssuingFilterFields({
  view,
  window,
  onChange,
  selectedItemLabel,
}: FilterFieldsProps) {
  const { t } = useTranslation();

  // Only granularities that draw a readable number of columns over this range are
  // offered. The alternative — offering "Day" for a five-year window and
  // silently substituting something else — makes the control lie about what it
  // does.
  const granularities = BUCKET_GRANULARITIES.filter((bucket) => granularityFits(window, bucket));

  return (
    <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-end">
      <Control label={t('analytics.granularity.label')}>
        <Segmented<BucketGranularity>
          value={window.bucket}
          onChange={(bucket) => onChange({ bucket })}
          options={granularities.map((bucket) => ({
            value: bucket,
            labelKey: GRANULARITY_LABEL_KEY[bucket],
          }))}
          ariaLabel={t('analytics.granularity.label')}
        />
      </Control>

      <Control label={t('analytics.measureLabel')}>
        <Segmented<MeasureId>
          value={view.measure}
          onChange={(measure) => onChange({ measure })}
          options={MEASURE_IDS.map((id) => ({ value: id, labelKey: MEASURES[id].labelKey }))}
          ariaLabel={t('analytics.measureLabel')}
        />
      </Control>

      <Control label={t('analytics.itemTypes')}>
        <ItemTypeFilter
          value={view.itemTypes}
          onChange={(itemTypes) => onChange({ itemTypes })}
        />
      </Control>

      <Control label={t('analytics.medicine')} className="min-w-[240px] flex-1">
        <MedicinePicker
          itemId={view.itemId}
          itemLabel={selectedItemLabel}
          onSelect={(itemId) => onChange({ itemId })}
        />
      </Control>
    </div>
  );
}

function Control({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <span className="text-[11.5px] font-medium uppercase tracking-[0.06em] text-muted">
        {label}
      </span>
      {children}
    </div>
  );
}

interface ItemTypeFilterProps {
  value: readonly ItemType[];
  onChange: (value: ItemType[]) => void;
}

/**
 * The item-type filter, drawn with the same pills the charts key their colours
 * to. An unselected type is the pill at rest under a neutral border; a selected
 * one is ringed. Using anything else here would mean the filter and the legend
 * spoke about the same five categories in two visual languages.
 */
export function ItemTypeFilter({ value, onChange }: ItemTypeFilterProps) {
  const { t } = useTranslation();
  const empty = value.length === 0;

  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={t('analytics.itemTypes')}>
      {ITEM_TYPES.map((type) => {
        const on = value.includes(type);
        return (
          <button
            key={type}
            type="button"
            aria-pressed={on}
            onClick={() =>
              onChange(on ? value.filter((v) => v !== type) : [...value, type])
            }
            className={cn(
              'rounded-full transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
              on ? 'ring-2 ring-ink ring-offset-1 ring-offset-background' : 'opacity-70 hover:opacity-100',
            )}
          >
            <TypePill type={type} size="md" />
          </button>
        );
      })}
      {!empty && (
        <button
          type="button"
          onClick={() => onChange([])}
          className="inline-flex h-[22px] items-center gap-1 rounded-full px-1.5 text-[11.5px] text-muted hover:text-ink"
        >
          <Icon name="x" size={11} strokeWidth={2.2} />
          {t('analytics.itemTypesAll')}
        </button>
      )}
    </div>
  );
}

interface MedicinePickerProps {
  itemId: string | null;
  itemLabel: string | null;
  onSelect: (itemId: string | null) => void;
}

const SEARCH_MIN_CHARS = 2;

/**
 * The specific-medicine control.
 *
 * Searches the clinic's own items rather than the medicine reference catalogue:
 * the filter's value is an item id, and a reference row has none — offering one
 * would be offering a choice that cannot be applied.
 *
 * Selected, it collapses to a single removable token. That is the "unmistakable,
 * one click to clear" state the narrowed page needs: while it is set, every
 * panel above is about one medicine, and the way back has to be impossible to
 * miss.
 */
export function MedicinePicker({ itemId, itemLabel, onSelect }: MedicinePickerProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const debounced = useDebounced(text.trim(), 200);

  const { data: results = [], isFetching } = useItemsSearch({
    query: debounced,
    limit: 8,
    enabled: itemId === null && debounced.length >= SEARCH_MIN_CHARS,
  });

  const combobox = useCombobox<Item>({
    text,
    onTextChange: setText,
    options: results,
    optionKey: (item) => item.id,
    onSelect: (item) => {
      onSelect(item.id);
      setText('');
    },
    minChars: SEARCH_MIN_CHARS,
    showsEmptyState: true,
    ariaLabel: t('analytics.medicine'),
  });

  if (itemId !== null) {
    return (
      <div className="flex min-h-[40px] items-center gap-2.5 rounded-md border border-ink bg-paper px-3 py-1.5">
        <Icon name="pill" size={15} className="shrink-0 text-muted" />
        <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold tracking-[-0.01em]">
          {itemLabel ?? t('analytics.medicineUnknown')}
        </span>
        <button
          type="button"
          onClick={() => onSelect(null)}
          aria-label={t('analytics.medicineClear')}
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-paper-3 text-muted hover:text-ink"
        >
          <Icon name="x" size={12} strokeWidth={2.2} />
        </button>
      </div>
    );
  }

  return (
    <div ref={combobox.rootRef} className="relative">
      <div
        ref={combobox.anchorRef}
        className="flex min-h-[40px] items-center gap-2 rounded-md border border-line-2 bg-paper px-3"
      >
        <Icon name="search" size={15} className="shrink-0 text-muted" />
        <input
          {...combobox.inputProps}
          aria-label={t('analytics.medicine')}
          placeholder={t('analytics.medicineSearch')}
          className="h-[38px] min-w-0 flex-1 bg-transparent text-[13.5px] text-ink outline-none placeholder:text-muted-2"
        />
      </div>

      <ComboboxListbox
        {...combobox.listboxProps}
        emptyState={isFetching ? t('common.searching') : t('analytics.medicineNoMatch')}
        renderOption={(item) => (
          <>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13.5px] font-semibold tracking-[-0.01em]">
                {item.brandName}
              </div>
              <div className="truncate text-[12px] text-muted">
                {item.genericName}
                {item.strength ? ` \u00b7 ${item.strength}` : ''}
              </div>
            </div>
            <TypePill type={item.type} size="sm" />
          </>
        )}
      />
    </div>
  );
}
